import { FiBell, FiLogOut, FiChevronDown, FiCheckSquare, FiMessageSquare, FiUser, FiBriefcase, FiX, FiTrash2, FiMenu } from "react-icons/fi";
import { useAuth } from "../../context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { routeTitles } from "../../config/routes";
import { useState, useEffect, useRef } from "react";
import "./Topbar.css";
import { useStatus } from "../../context/StatusContext";
import { resolveImageUrl } from "../../utils/resolveImageUrl";
import { formatRoleLabel } from "../../utils/roles";

const ALLOWED_STATUSES = ["active", "break", "remote", "dayoff", "offline"];

export default function Topbar({ onMenuClick, sidebarOpen }) {
  const { currentUser, logout, totalUnreadCount, markRoomRead } = useAuth();
  const { getUserStatus, setUserStatus, STATUS_OPTIONS } = useStatus();

  const navigate = useNavigate();
  const location = useLocation();

  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);

  const [avatarSrc, setAvatarSrc] = useState(null);

  useEffect(() => {
    const update = () => {
      try {
        const stored = localStorage.getItem("user");
        if (!stored) return;
        const user = JSON.parse(stored);
        const url = user?.avatar || user?.profilePicture?.url || user?.photo;
        setAvatarSrc(url ? resolveImageUrl(url) : null);
      } catch {
        // ignore
      }
    };

    update();
    window.addEventListener("userChanged", update);
    return () => window.removeEventListener("userChanged", update);
  }, []);

  useEffect(() => {
    const url = currentUser?.avatar || currentUser?.profilePicture?.url || currentUser?.photo;
    if (!url) { setAvatarSrc(null); return; }
    const hasTimestamp = url.includes("?t=");
    const final = hasTimestamp ? resolveImageUrl(url) : `${resolveImageUrl(url)}?t=${Date.now()}`;
    setAvatarSrc(final);
  }, [currentUser?.avatar, currentUser?.profilePicture?.url, currentUser?.photo]);

  const [notifications, setNotifications] = useState([
    { id: 1, type: "task",    text: "New task assigned: Design Homepage",  time: "5 min ago",   read: false, icon: FiCheckSquare,   color: "#22c55e" },
    { id: 2, type: "message", text: "New message from Ahmed in Team Chat", time: "15 min ago",  read: false, icon: FiMessageSquare, color: "#8b5cf6" },
    { id: 3, type: "user",    text: "New user registered: Sara Ali",       time: "1 hour ago",  read: true,  icon: FiUser,          color: "#3b82f6" },
    { id: 4, type: "project", text: "Project Alpha deadline in 2 days",    time: "3 hours ago", read: true,  icon: FiBriefcase,     color: "#f59e0b" },
  ]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const markAsRead  = (id) => setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
  const markAllRead = ()   => setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  const deleteNotif = (id) => setNotifications((prev) => prev.filter((n) => n.id !== id));

  const notifRef = useRef(null);
  useEffect(() => {
    const handleClick = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const userId = currentUser?._id || currentUser?.id;

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const pageTitle = routeTitles[location.pathname] || "Dashboard";

  const userName =
    currentUser?.userName ||
    currentUser?.user?.userName ||
    currentUser?.email?.split("@")[0] ||
    "User";

  const status = userId ? getUserStatus(userId) : "offline";

  const filteredOptions = STATUS_OPTIONS.filter((opt) =>
    ALLOWED_STATUSES.includes(opt.value)
  );

  const remoteExists = filteredOptions.some((o) => o.value === "remote");
  const displayOptions = remoteExists
    ? filteredOptions
    : [
        ...filteredOptions.filter((o) => o.value !== "offline" && o.value !== "dayoff"),
        { value: "remote", label: "Remote", color: "#06b6d4" },
        ...filteredOptions.filter((o) => o.value === "dayoff" || o.value === "offline"),
      ];

  const currentStatus =
    displayOptions.find((s) => s.value === status) ||
    displayOptions.find((s) => s.value === "offline") ||
    { value: "offline", label: "Offline", color: "#6b7280" };

  return (
    <header className="topbar">
      {/* LEFT */}
      <div className="topbar-left">
        <button
          className="menu-toggle-btn"
          onClick={onMenuClick}
          style={{ display: sidebarOpen ? "none" : "flex" }}
        >
          <FiMenu size={24} />
        </button>
        <div className="topbar-left-icons">
          {/* ✅ message icon — بيستخدم totalUnreadCount من AuthContext */}
          {!sidebarOpen && (
            <button
              className="message-icon-btn"
              onClick={() => {
                navigate("/messages");
                // ✅ مش محتاج resetUnread — AuthContext بيصفّر تلقائي لما تفتح الشات
              }}
              style={{ position: "relative", background: "none", border: "none", color: "#9ca3af", cursor: "pointer", padding: "8px", marginLeft: "8px" }}
            >
              <FiMessageSquare size={20} />
              {totalUnreadCount > 0 && (
                <span className="topbar-unread-badge">{totalUnreadCount}</span>
              )}
            </button>
          )}
        </div>
        <h2 className="page-title">{pageTitle}</h2>
      </div>

      {/* RIGHT */}
      <div className="topbar-right">

        {/* NOTIFICATIONS – hidden; restore by changing false → true */}
        {false && (
          <div className="notification-wrapper" ref={notifRef}>
            <button
              className="topbar-icon notification-btn"
              onClick={() => {
                setShowNotifications((prev) => !prev);
                setShowStatusMenu(false);
              }}
            >
              <FiBell />
              {unreadCount > 0 && (
                <span className="notification-badge">{unreadCount}</span>
              )}
            </button>

            {showNotifications && (
              <div className="notification-dropdown">
                <div className="notification-header">
                  <h3>Notifications</h3>
                  <div className="notification-actions">
                    {unreadCount > 0 && (
                      <button className="mark-all-btn" onClick={markAllRead}>
                        Mark all read
                      </button>
                    )}
                    <button className="close-btn" onClick={() => setShowNotifications(false)}>
                      <FiX size={18} />
                    </button>
                  </div>
                </div>

                <div className="notification-list">
                  {notifications.length === 0 ? (
                    <div className="notification-empty">
                      <FiBell size={32} />
                      <p>No notifications</p>
                    </div>
                  ) : (
                    notifications.map((n) => (
                      <div
                        key={n.id}
                        className={`notification-item ${n.read ? "read" : "unread"}`}
                        onClick={() => {
                          markAsRead(n.id);
                          if (n.type === "task")    navigate("/tasks");
                          if (n.type === "message") navigate("/messages");
                          if (n.type === "user")    navigate("/users");
                          if (n.type === "project") navigate("/projects");
                          setShowNotifications(false);
                        }}
                      >
                        <div
                          className="notification-icon"
                          style={{ background: `${n.color}20`, color: n.color }}
                        >
                          <n.icon size={16} />
                        </div>
                        <div className="notification-content">
                          <p className="notification-text">{n.text}</p>
                          <span className="notification-time">{n.time}</span>
                        </div>
                        <button
                          className="notification-delete"
                          onClick={(e) => { e.stopPropagation(); deleteNotif(n.id); }}
                        >
                          <FiTrash2 size={14} />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* STATUS DROPDOWN */}
        <div className="status-wrapper">
          <button
            className="status-badge"
            style={{
              color: currentStatus?.color,
              borderColor: `${currentStatus?.color}55`,
              backgroundColor: `${currentStatus?.color}18`,
            }}
            onClick={() => setShowStatusMenu((prev) => !prev)}
          >
            <span className="status-dot" style={{ backgroundColor: currentStatus?.color }} />
            {currentStatus?.label}
            <FiChevronDown size={12} />
          </button>

          {showStatusMenu && (
            <div className="status-dropdown">
              {displayOptions.map((opt) => (
                <button
                  key={opt.value}
                  className={`status-option ${status === opt.value ? "selected" : ""}`}
                  onClick={() => {
                    if (userId) {
                      setUserStatus(userId, opt.value, {
                        userName: currentUser?.userName,
                        department: currentUser?.department,
                      });
                    }
                    setShowStatusMenu(false);
                  }}
                >
                  <span className="status-dot" style={{ backgroundColor: opt.color }} />
                  {opt.label}
                  {status === opt.value && <span className="status-check">✓</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* USER */}
        <div className="user-container">
          <div className="user-avatar-wrapper">
            <div className="user-avatar">
              {avatarSrc ? (
                <img
                  src={avatarSrc}
                  alt="avatar"
                  className="topbar-avatar-img"
                  key={avatarSrc}
                />
              ) : (
                <span>{userName?.charAt(0)?.toUpperCase()}</span>
              )}
            </div>
            <span
              className="avatar-status-dot"
              style={{ backgroundColor: currentStatus?.color }}
            />
          </div>
          <div className="user-details">
            <span className="user-name">{userName}</span>
            <span className="user-role">{formatRoleLabel(currentUser?.role)}</span>
          </div>
        </div>

        {/* LOGOUT */}
        <button onClick={handleLogout} className="logout-button">
          <FiLogOut />
          <span>Logout</span>
        </button>
      </div>
    </header>
  );
}